var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo, isValidElement, cloneElement, useRef, useEffect, } from "react";
import { withPortal } from "../../hoc/withPortal";
import useUniqueId from "../../hooks/useUniqueId";
import Tooltip from "../tooltip/Tooltip";
import ActionMenu from "../menu/ActionMenu";
import { ErrorTooltip } from "../menu/KebabActionMenu";
import Box from "../box/Box";
import { useAsyncActionMenu } from "../menu/useAsyncActions";
import { caretPosition, caretWidthDiff } from "../tooltip/Tooltip.style";
import useIdAttrs from "../../hooks/useIdAttrs";
import Text from "../text/Text";
import mergeProps from "../../utils/mergeProps";
const prefixId = "nx-with-actionmenu-";
const tooltipSuffixId = "-menu";
// eslint-disable-next-line complexity
export const TargetContent = (_a) => {
    var { id, children, forwardedRef, onClick, onMouseEnter, onMouseLeave, onPointerEnter, onPointerLeave, onKeyDown, onFocus, onBlur, onContextMenu, tabIndex, 
    // unused props
    maxWidth, hide, isVisible, tooltipPosition, show, update, trigger, onMenuVisibilityChange, actions, "aria-label": ariaLabel } = _a, rest = __rest(_a, ["id", "children", "forwardedRef", "onClick", "onMouseEnter", "onMouseLeave", "onPointerEnter", "onPointerLeave", "onKeyDown", "onFocus", "onBlur", "onContextMenu", "tabIndex", "maxWidth", "hide", "isVisible", "tooltipPosition", "show", "update", "trigger", "onMenuVisibilityChange", "actions", "aria-label"]);
    const eventHandlers = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, (onClick && { onClick })), (onKeyDown && { onKeyDown })), (onFocus && { onFocus })), (onBlur && { onBlur })), (onMouseEnter && { onMouseEnter })), (onMouseLeave && { onMouseLeave })), (onPointerEnter && { onPointerEnter })), (onPointerLeave && { onPointerLeave })), (onContextMenu && { onContextMenu }));
    const isMounted = useRef(false);
    useEffect(() => {
        if (isMounted.current) {
            onMenuVisibilityChange === null || onMenuVisibilityChange === void 0 ? void 0 : onMenuVisibilityChange(isVisible);
        }
        else {
            isMounted.current = true;
        }
    }, [isVisible]);
    const commonProps = Object.assign(Object.assign({ ref: forwardedRef, "aria-haspopup": "menu", "aria-hidden": false, "aria-expanded": isVisible, "data-visible": isVisible, tabIndex }, rest), { "aria-controls": `${id}_actions`, "data-insights": rest["data-insights"] && `${rest["data-insights"]}_button` });
    if (typeof children === "string") {
        return (React.createElement(Text, Object.assign({}, commonProps, eventHandlers), children));
    }
    if (typeof children === "function") {
        return cloneElement(children(Object.assign(Object.assign({}, commonProps), eventHandlers)), {
            ref: forwardedRef,
        });
    }
    const mergedProps = isValidElement(children)
        ? mergeProps(Object.assign(Object.assign(Object.assign({}, children.props), (!children.props["aria-label"] && { "aria-label": "actions" })), { onKeyDown: (e) => { var _a, _b; return (e.key === "Enter" || e.key === " ") && ((_b = (_a = children.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e)); } }), eventHandlers)
        : eventHandlers;
    return cloneElement(children, Object.assign(Object.assign(Object.assign(Object.assign({}, commonProps), mergedProps), (children.props.tabIndex && { tabIndex: children.props.tabIndex })), (!children.props.id && { id: `${id}_target` })));
};
export const ActionTooltip = (_a) => {
    var { hide, tooltipPosition, actions, id, isVisible, "data-insights": insightsAttr, handleQuickFind, update, onMenuVisibilityChange } = _a, rest = __rest(_a, ["hide", "tooltipPosition", "actions", "id", "isVisible", "data-insights", "handleQuickFind", "update", "onMenuVisibilityChange"]);
    const { asyncActions, isError } = useAsyncActionMenu(isVisible, actions, update);
    const idAttrs = useIdAttrs({ id, insightsAttr });
    if (isError) {
        return (React.createElement(Box, Object.assign({}, idAttrs("_actions")),
            React.createElement(ErrorTooltip, { tooltipPosition: tooltipPosition })));
    }
    if (asyncActions && Array.isArray(asyncActions) && asyncActions.length > 0) {
        return (React.createElement(Tooltip, Object.assign({}, idAttrs(tooltipSuffixId), { position: tooltipPosition, variant: "action" }),
            React.createElement(ActionMenu, Object.assign({ handleQuickFind: handleQuickFind, "aria-label": "menu" }, idAttrs("_actions"), { onSelect: (_, e) => {
                    // return focus when selected with keyboard
                    hide(1, e.type === "keydown");
                }, onHide: () => {
                    hide(1, true);
                } }, rest), asyncActions)));
    }
    return React.createElement(React.Fragment, null);
};
const WithActionMenu = (_a) => {
    var { actions, alignment = "bottomRight", position = "absolute", trigger = "click", hide = true, offset = [caretPosition - caretWidthDiff / 2, 8], maxWidth, flip = true, disablePortal = false, children } = _a, rest = __rest(_a, ["actions", "alignment", "position", "trigger", "hide", "offset", "maxWidth", "flip", "disablePortal", "children"]);
    const portalId = useUniqueId(prefixId);
    const TargetWithPortal = useMemo(() => withPortal(ActionTooltip, Object.assign({ useFocusTrap: false, allowTabNavigation: false, alignment,
        trigger,
        position,
        offset,
        flip,
        hide,
        disablePortal }, rest))(TargetContent), [alignment, position, ...offset, trigger, flip, hide, disablePortal]);
    return (React.createElement(TargetWithPortal, Object.assign({ id: portalId, actions: actions, maxWidth: maxWidth, trigger: trigger }, rest), children));
};
export default WithActionMenu;
//# sourceMappingURL=WithActionMenu.js.map